import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ProvinceApi } from 'api';
import { RootState } from 'app';

export const findProvincesAndDistricts = createAsyncThunk(
  'location/getProvincesAndDistricts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ProvinceApi.findMasterData();
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface LocationState {
  provinceSelectCodeData: Array<any>;
  provinceData: Array<any>;
  districtData: Array<any>;
  wardData: Array<any>;
  data: Array<any>;
  isLoaded: boolean;
  findLocation: {
    provinceCode: string;
    districtCode: string;
    wardCode: string;
    address: string;
    provinceName?: string;
    districtName?: string;
    wardName?: string;
    latitude?: string;
    longitude?: string;
  } | null;
}

const initialState: LocationState = {
  provinceSelectCodeData: [],
  provinceData: [],
  districtData: [],
  wardData: [],
  data: [],
  isLoaded: false,
  findLocation: null,
};

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setIsLoaded(state) {
      state.isLoaded = true;
    },
    setProvinceSelect(state, action: PayloadAction<string>) {
      state.districtData = state.data.find(
        item => item.code == action.payload
      )?.districts;
      state.wardData = [];
    },
    setDistrictSelect(
      state,
      action: PayloadAction<{ provinceCode: string; districtCode: string }>
    ) {
      state.districtData = state.data.find(
        item => item.code == action.payload.provinceCode
      )?.districts;

      const findProvince = state.data.find(
        item => item.code == action.payload.provinceCode
      );
      const findDistrict = findProvince?.districts.find(
        (item: any) => item.code == action.payload.districtCode
      );
      state.wardData = findDistrict?.wards || [];
    },
    resetLocationSearch(state) {
      state.findLocation = null;
    },
    findLocationValue(
      state,
      action: PayloadAction<{
        provinceName: string;
        districtName: string;
        wardName: string;
        address: string;
        latitude?: string;
        longitude?: string;
      }>
    ) {
      const provinceFind = state.data.find(item =>
        item.fullName
          .toLowerCase()
          .includes(action.payload.provinceName?.toLowerCase())
      );

      const districtFind =
        provinceFind &&
        provinceFind.districts.find((item: any) =>
          item.fullName
            .toLowerCase()
            .includes(action.payload.districtName?.toLowerCase())
        );
      const wardFind =
        districtFind &&
        districtFind.wards.find((item: any) =>
          item.fullName
            .toLowerCase()
            .includes(action.payload.wardName?.toLowerCase())
        );

      if (provinceFind && districtFind && wardFind) {
        state.findLocation = {
          provinceCode: provinceFind.code,
          provinceName: provinceFind.fullName,
          districtCode: districtFind.code,
          districtName: districtFind.fullName,
          wardCode: wardFind.code,
          wardName: wardFind.fullName,
          address: action.payload.address,
          latitude: action.payload.latitude,
          longitude: action.payload.longitude,
        };
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(findProvincesAndDistricts.fulfilled, (state, action) => {
      const { data }: any = action.payload;

      state.provinceData = data.map((item: any) => {
        return {
          id: item.id,
          fullName: item.fullName,
          code: item.code,
          value: item.id,
          name: item.name,
        };
      });

      state.provinceSelectCodeData = data.map((item: any) => {
        return {
          id: item.id,
          fullName: item.fullName,
          code: item.code,
          value: item.code,
          name: item.name,
        };
      });

      state.data = data;
      state.isLoaded = true;
    });
  },
});

const { actions, reducer: locationReducer } = locationSlice;

export const {
  setProvinceSelect,
  setDistrictSelect,
  findLocationValue,
  resetLocationSearch,
} = actions;

export default locationReducer;

export const selectLocation = (state: RootState): LocationState =>
  state.location as LocationState;
